import { WebPortalService } from "../resource";

const AuthModule = {
  namespaced: true,
  state: {
    data: [],
    user: {
      id: "",
      username: "",
      name: "",
      email: "",
      group: "",
      corporate: "",
      corporate_name: ""
    },
    // NOT part of `user`. The EAS-migration right comes from the API's
    // config.yml allow-list, never from the user record (#47), so it is not a
    // profile attribute and must not ride along on profile responses — those
    // do not carry it, and folding it into SET_USER made saving your own name
    // revoke your access for the rest of the session.
    migration: false,
    // Bumped on every identity change (sign-in, sign-out). refreshPermission
    // captures it before its request and discards its own answer if it moved
    // while that request was in flight (#49): axios allows 120s per request, so
    // a stalled /v4/permission belonging to the previous session could
    // otherwise land after the next user has signed in and hand them the
    // previous user's rights.
    authEpoch: 0,
    locale: "th",
    maintenanceKey: "",
    maintenanceEnabled: false,
    maintenanceCheckedAt: 0
  },
  mutations: {
    SET_LOCALE(state, data) {
      state.locale = data;
    },
    SET_DATA(state, data) {
      state.data = data;
    },
    SET_USER(state, data) {
      state.user.id = data.Id;
      state.user.username = data.Username;
      state.user.name = data.Name;
      state.user.email = data.Email;
      state.user.group = data.GroupId;
      state.user.corporate = data.EnterpriseId;
      state.user.corporate_name = data.EnterpriseName;
      state.authEpoch++;
    },
    CLEAR_USER(state, data) {
      state.user.id = "";
      state.user.username = "";
      state.user.name = "";
      state.user.email = "";
      state.user.group = "";
      state.user.corporate = "";
      state.user.corporate_name = "";
      // Signing out drops the right as well as the profile.
      state.migration = false;
      state.authEpoch++;
    },
    SET_MIGRATION(state, migration) {
      state.migration = !!migration;
    },
    SET_MAINTENANCE_KEY(state, key) {
      state.maintenanceKey = key || "";
    },
    CLEAR_MAINTENANCE_KEY(state) {
      state.maintenanceKey = "";
    },
    SET_MAINTENANCE_STATUS(state, enabled) {
      state.maintenanceEnabled = !!enabled;
      state.maintenanceCheckedAt = Date.now();
    }
  },
  actions: {
    ForgotPassword({ commit, state }, data) {
      return WebPortalService.ForgotPassword(data);
    },
    ResetPassword({ commit, state }, data) {
      return WebPortalService.ResetPassword(data);
    },
    setUser({ commit, state }, data) {
      commit("SET_USER", data);
    },
    // Stamped explicitly at login from the login response's User.Migration,
    // rather than implicitly inside SET_USER: only the login endpoints carry
    // the flag, so the one call site that has it says so out loud.
    setMigration({ commit }, migration) {
      commit("SET_MIGRATION", migration);
    },
    getProfile({ commit, state }, data) {
      return WebPortalService.GetProfile(data);
    },
    createData({ commit, state }, data) {
      return WebPortalService.AuthCreate(data);
    },
    updateData({ commit, state }, data) {
      return WebPortalService.AuthUpdate(data);
    },
    deleteData({ commit, state }, data) {
      return WebPortalService.AuthDelete(data);
    },
    changeProfile({ commit, state }, data) {
      return WebPortalService.ChangeProfile(data);
    },
    changePassword({ commit, state }, data) {
      return WebPortalService.ChangePassword(data);
    },
    SignIn({ commit, state }, data) {
      return WebPortalService.SignIn(data);
    },
    async listData({ commit, state }) {
      try {
        const data = await WebPortalService.AuthList();
        if (data.data.Data == null) {
          commit("SET_DATA", []);
        } else {
          commit("SET_DATA", data.data.Data);
        }
      } catch {
        commit("SET_DATA", []);
      }
    },
    async signOut({ commit, state }) {
      try {
        await WebPortalService.SignOut();
        commit("CLEAR_USER", state.user);
      } catch {
        commit("CLEAR_USER", state.user);
      }
    },
    setLocale({ commit, state }, locale) {
      commit("SET_LOCALE", locale);
    },
    async checkMaintenanceStatus({ commit, state }) {
      const MAX_AGE_MS = 30 * 1000;
      if (Date.now() - state.maintenanceCheckedAt < MAX_AGE_MS) {
        return state.maintenanceEnabled;
      }
      try {
        const res = await WebPortalService.MaintenanceStatus();
        commit("SET_MAINTENANCE_STATUS", res.data?.Enabled);
        return state.maintenanceEnabled;
      } catch {
        // network blip — keep last known value, don't lock users out
        return state.maintenanceEnabled;
      }
    },
    // Re-reads the EAS-migration allow-list from the API. The list lives in
    // config.yml and is consulted per request, so an operator added to it does
    // not have to log out — this refresh picks the change up.
    //
    // On any failure the last known value is kept: a network blip must never
    // silently revoke access (same rule as checkMaintenanceStatus above). A
    // stale answer, on the other hand, must never GRANT it — hence the epoch
    // check below (#49).
    async refreshPermission({ commit, state }) {
      const epoch = state.authEpoch;
      try {
        const res = await WebPortalService.GetPermission();
        // Somebody signed out, or signed in, while this was in flight. The
        // answer describes a session that is no longer the current one, so it
        // cannot be applied to this one — whoever is signed in now already
        // stamped their own value from their own login response.
        if (state.authEpoch !== epoch) {
          return state.migration;
        }
        const migration = res.data?.Data?.Migration;
        if (migration !== undefined && migration !== null) {
          commit("SET_MIGRATION", migration);
        }
        return state.migration;
      } catch {
        return state.migration;
      }
    },
    setMaintenanceKey({ commit }, key) {
      commit("SET_MAINTENANCE_KEY", key);
    },
    clearMaintenanceKey({ commit }) {
      commit("CLEAR_MAINTENANCE_KEY");
    }
  },
  getters: {
    getData(state) {
      return state.data;
    },
    getLocale(state) {
      return state.locale;
    },
    getUser(state) {
      return state.user;
    },
    isCorporateSuperAdmin(state) {
      return state.user.group == 4;
    },
    isDtacAdmin(state) {
      return state.user.group == 3;
    },
    isCorporateAdmin(state) {
      return state.user.group == 2;
    },
    isReadOnly(state) {
      return state.user.group == 1;
    },
    // Not a GroupId check: membership of the config.yml allow-list is the only
    // gate on the EAS Migration module (#47).
    isMigrationUser(state) {
      return state.migration === true;
    },

    isCorporateSuperAdminMenu(state) {
      return state.user.group == 4;
    },
    isDtacAdminMenu(state) {
      return state.user.group == 3;
    },
    isCorporateAdminMenu(state) {
      return state.user.group == 2 || state.user.group == 4;
    },
    isReadOnlyMenu(state) {
      return state.user.group == 1;
    },
    isMigrationUserMenu(state) {
      return state.migration === true;
    },

    isAuthenticate(state) {
      return state.user.id != "";
    },
    getMaintenanceKey(state) {
      return state.maintenanceKey;
    },
    isMaintenanceActive(state) {
      return state.maintenanceEnabled;
    }
  }
};

export default AuthModule;
